<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('attributes.payment')"
    size="large"
    @close="$emit('close')"
  >
    <template #body>
      <PaymentModalWizard
        v-model:payment="payment"
        v-model:step="step"
        v-model:cheque-duplicate="cheque_duplicate"
        :place-holder-issuer="null"
        :place-holder-direct-debit="dossier?.concernedPerson?.full_name"
        :dossier-contacts="dossier.contacts"
        :currency="currency"
        :form-submitting="formSubmitting"
        :custom-payment-modes="custom_payment_modes"
        :has-dossier="true"
        :with-other-section="true"
        :funeral-home-id="dossier.funeral_home_id"
        @submit:payment="submitForm"
      />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import PaymentModalWizard from '@ui/modal/payment/components/PaymentModalWizard.vue'
import { duplicateFromError } from '@ui/modal/payment/chequeNumberDuplicate'

/** Wizard step holding the cheque number. */
const AMOUNT_STEP = 2

export default {
  name: 'PaymentDossierFormModal',
  components: {
    PaymentModalWizard,
    BaseCenteredModal
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    paymentId: {
      type: [String, Number],
      required: false,
      default: null
    },
    editBank: {
      type: [Number],
      required: false,
      default: 0
    },
    dossier: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'update:is-modal-open'],
  data() {
    return {
      step: 0,
      cheque_duplicate: null,
      formSubmitting: false,
      custom_payment_modes: [],
      dossier_contacts: [],
      payment: null
    }
  },
  computed: {
    ...mapGetters({
      currencySettings: 'metadata/getDefaultCurrencyMetadata'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    },
    currency() {
      return this.dossier?.funeral_home?.currency_code ?? this.currencySettings
    }
  },
  watch: {
    isModalOpen(newValue, oldValue) {
      if (newValue) {
        if (!this.paymentId) {
          this.processFetchNewPaymentAction({
            dossier_id: this.dossier.id
          }).then((response) => {
            this.handleFetchApiResponse(response)
          })
        } else {
          this.processFetchPaymentByIdAction({
            dossier_id: this.dossier.id,
            payment_id: this.paymentId
          }).then((response) => {
            this.handleFetchApiResponse(response)
          })
        }
      } else {
        this.payment = null
        this.step = 0
        this.cheque_duplicate = null
      }
    }
  },
  methods: {
    /**
     * Brings the user back to the cheque number when the save was refused
     * because another cheque already carries it. The form stays open on any
     * failure: closing it would throw away everything typed.
     */
    showChequeDuplicate(reason) {
      const duplicate = duplicateFromError(reason)

      if (duplicate) {
        this.cheque_duplicate = duplicate
        this.step = AMOUNT_STEP
      }
    },
    ...mapActions({
      processFetchNewPaymentAction: 'invoicing/fetchNewPayment',
      processStoreNewPaymentToDossierAction: 'dossier/storeNewPaymentToDossier',
      processFetchPaymentByIdAction: 'invoicing/fetchPaymentById',
      processUpdatePaymentByIdAction: 'invoicing/updatePaymentById'
    }),
    handleFetchApiResponse(response) {
      this.payment = {
        ...response.payment,
        payment_type: 'CRE',
        tier_payant: 1,
        payment_status: 'AWA'
      }
      this.custom_payment_modes = response.metadata.payment_modes ?? []
      if (
        (this.$can('super-admin') || this.$can('manager')) &&
        this.custom_payment_modes.length > 0
      ) {
        this.custom_payment_modes.push({
          id: 'custom',
          denomination: {
            [this.$i18n.locale]: this.$t('global.customize')
          }
        })
        this.payment.payment_mode_id = this.payment.payment_mode_id ?? 'custom'
      }
      if (this.custom_payment_modes.length === 0) {
        this.payment.payment_mode_id = 'custom'
      }
    },
    submitForm() {
      if (this.payment_modes?.length > 0 && !this.payment.payment_mode_id) {
        this.payment_mode_error = true
        return
      }
      this.formSubmitting = true
      if (!this.paymentId) {
        this.processStoreNewPaymentToDossierAction({
          dossier_id: this.dossier.id,
          payment: this.payment
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_added_to_dossier'))
            this.localIsModalOpen = false
            this.formSubmitting = false
            this.payment_mode_error = false
          })
          .catch((reason) => {
            this.formSubmitting = false
            this.showChequeDuplicate(reason)
          })
      } else {
        this.processUpdatePaymentByIdAction({
          dossier_id: this.dossier.id,
          payment_id: this.payment.id,
          payment: this.payment
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_updated'))
            this.localIsModalOpen = false
            this.formSubmitting = false
            this.payment_mode_error = false
          })
          .catch((reason) => {
            this.formSubmitting = false
            this.showChequeDuplicate(reason)
          })
      }
    }
  }
}
</script>
